import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmt, fmtInt } from '../lib/format';
import {
  Wallet, CurrencyDollar, TrendUp, Receipt, Users, Package,
  Bank, Warning, ChartLineUp, ArrowRight,
} from '@phosphor-icons/react';

const SCOPES = [
  { v: 'today', label: '今日' },
  { v: 'week', label: '本周' },
  { v: 'month', label: '本月' },
];

function StatCard({ icon: Icon, label, value, suffix, accent = 'indigo', sub }) {
  const accents = {
    indigo: 'from-indigo-500/20 to-indigo-500/0 text-indigo-300 border-indigo-500/30',
    emerald: 'from-emerald-500/20 to-emerald-500/0 text-emerald-300 border-emerald-500/30',
    amber: 'from-amber-500/20 to-amber-500/0 text-amber-300 border-amber-500/30',
    cyan: 'from-cyan-500/20 to-cyan-500/0 text-cyan-300 border-cyan-500/30',
    rose: 'from-rose-500/20 to-rose-500/0 text-rose-300 border-rose-500/30',
  };
  return (
    <div className="card-base p-5 hover:border-slate-600 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <span className="label-tiny">{label}</span>
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${accents[accent]} border flex items-center justify-center`}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <div className="font-num text-2xl sm:text-3xl font-semibold text-white tracking-tight">
        {value}<span className="text-base text-slate-500 ml-1.5">{suffix}</span>
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [scope, setScope] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (s) => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard', { params: { scope: s } });
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(scope); }, [scope]);

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">仪表盘</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {isAdmin ? '业务总览 · 实时数据' : `欢迎回来,${user?.name}`}
          </p>
        </div>
        <div className="inline-flex rounded-lg bg-slate-900/50 border border-slate-800 p-1">
          {SCOPES.map((s) => (
            <button
              key={s.v}
              data-testid={`scope-${s.v}`}
              onClick={() => setScope(s.v)}
              className={`px-3 sm:px-4 py-1.5 text-sm rounded-md transition-all ${
                scope === s.v
                  ? 'bg-indigo-600 text-white font-medium shadow shadow-indigo-900/30'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="card-base p-12 text-center text-slate-500">加载中…</div>
      )}

      {data && !loading && (
        <>
          {/* OWED orders warning (top of page if any owed) */}
          {data.owed_count > 0 && (
            <Link to="/orders?owed=1" className="block">
              <div className="card-base p-4 sm:p-5 border-rose-500/40 hover:border-rose-500/60 transition-colors pulse-warn" style={{
                background: 'linear-gradient(135deg, rgba(244,63,94,0.12), rgba(23,33,54,1))'
              }} data-testid="owed-warning-card">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-rose-500/20 border border-rose-500/40 flex items-center justify-center">
                      <Warning size={22} weight="duotone" className="text-rose-400" />
                    </div>
                    <div>
                      <div className="text-sm text-rose-300 font-semibold">🔴 待收款订单提醒</div>
                      <div className="text-xs text-slate-300 mt-0.5">
                        共 <span className="font-num font-semibold text-white">{data.owed_count}</span> 笔订单还没收到客户的钱
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {data.owed_total_myr > 0 && (
                      <div className="font-num text-lg font-semibold text-amber-300">RM {fmt(data.owed_total_myr)}</div>
                    )}
                    {data.owed_total_usdt > 0 && (
                      <div className="font-num text-sm text-emerald-300">+ {fmt(data.owed_total_usdt)} USDT</div>
                    )}
                    <div className="text-[10px] text-slate-500 mt-0.5">点击查看 →</div>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            <StatCard icon={Package} label="订单笔数" value={fmtInt(data.order_count)} suffix="笔" accent="indigo" sub={isAdmin && (data.offline_count !== undefined) ? `线下 ${data.offline_count} · 线上 ${data.online_count}` : undefined} />
            <StatCard icon={Users} label="活跃客户" value={fmtInt(data.active_customers)} suffix="位" accent="cyan" />
            <StatCard icon={CurrencyDollar} label="收 MYR" value={fmt(data.total_myr)} suffix="MYR" accent="amber" sub="线下 MYR 订单" />
            <StatCard icon={Wallet} label="收 USDT" value={fmt(data.total_usdt_in)} suffix="USDT" accent="emerald" sub="含线下直收 + 线上充值" />
            <StatCard icon={TrendUp} label="总营业额 Gross" value={fmt(data.total_gross_usdt)} suffix="USDT" accent="indigo" />
            <StatCard icon={Receipt} label="客户实收 Net" value={fmt(data.total_net_usdt)} suffix="USDT" accent="cyan" />
            {isAdmin && (
              <>
                <StatCard icon={ChartLineUp} label="汇率利润" value={fmt(data.total_exchange_profit_usdt)} suffix="USDT" accent="emerald" sub="MYR 订单产生" />
                <StatCard icon={Receipt} label="手续费利润" value={fmt(data.total_fee_usdt)} suffix="USDT" accent="emerald" sub="含线上充值差额" />
              </>
            )}
          </div>

          {/* Offline / Online split (admin only) */}
          {isAdmin && data.offline_count !== undefined && (data.offline_count > 0 || data.online_count > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="dashboard-type-split">
              <div className="card-base p-4 sm:p-5 border-indigo-500/20" style={{
                background: 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(23,33,54,1))'
              }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="label-tiny flex items-center gap-1.5">⛺ 线下充值</span>
                  <span className="text-xs text-slate-500 font-num">{data.offline_count} 笔</span>
                </div>
                <div className="font-num text-2xl font-semibold text-white">
                  {fmt(data.offline_gross_usdt || 0)} <span className="text-sm text-slate-500">USDT Gross</span>
                </div>
                <div className="text-sm text-emerald-400 font-num mt-1">
                  利润 {fmt(data.offline_profit_usdt || 0)} USDT
                </div>
              </div>
              <div className="card-base p-4 sm:p-5 border-sky-500/20" style={{
                background: 'linear-gradient(135deg, rgba(14,165,233,0.08), rgba(23,33,54,1))'
              }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="label-tiny flex items-center gap-1.5">🌐 线上充值</span>
                  <span className="text-xs text-slate-500 font-num">{data.online_count} 笔</span>
                </div>
                <div className="font-num text-2xl font-semibold text-white">
                  {fmt(data.online_gross_usdt || 0)} <span className="text-sm text-slate-500">USDT Gross</span>
                </div>
                <div className="text-sm text-emerald-400 font-num mt-1">
                  利润 {fmt(data.online_profit_usdt || 0)} USDT
                </div>
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="card-base p-5 sm:p-6 border-emerald-500/20" style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(23,33,54,1))'
            }}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="label-tiny mb-1">{SCOPES.find(s=>s.v===scope)?.label}订单净利润</div>
                  <div className="font-num text-4xl sm:text-5xl font-bold text-emerald-400 tracking-tight">
                    {fmt(data.total_profit_usdt)}
                    <span className="text-xl text-emerald-700 ml-2 font-medium">USDT</span>
                  </div>
                </div>
                <div className="text-xs text-slate-400 max-w-xs">
                  = 汇率利润 + 手续费利润<br/>
                  <span className="text-slate-500">(银行利润见月度报表)</span>
                </div>
              </div>
            </div>
          )}

          {/* Bank Reserves (admin only) */}
          {isAdmin && data.banks && data.banks.length > 0 && (
            <div className="card-base p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Bank size={20} weight="duotone" className="text-indigo-400" /> 银行储备
                </h2>
                <Link to="/banks" className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                  详情 <ArrowRight size={12} />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.banks.map((b) => {
                  const low = b.balance < b.low_balance_threshold;
                  return (
                    <div key={b.id} className={`p-4 rounded-lg border ${low ? 'border-amber-500/40 bg-amber-500/5 pulse-warn' : 'border-slate-700 bg-slate-900/40'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-xs text-slate-500 uppercase tracking-wider">银行 {b.name}</div>
                          <div className="font-num text-2xl font-semibold text-white mt-1">${fmt(b.balance)}</div>
                          <div className="text-xs text-slate-500 mt-1">
                            手续费 {b.bank_fee_percent}% · 客户费 {b.customer_fee_ref}%
                          </div>
                        </div>
                        {low && <Warning size={20} weight="fill" className="text-amber-400" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Customer ranking */}
          {data.customers && data.customers.length > 0 && (
            <div className="card-base p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users size={20} weight="duotone" className="text-cyan-400" /> 客户明细
              </h2>
              <div className="space-y-2">
                {data.customers.slice(0, 20).map((c) => (
                  <div key={c.email} className="p-3 sm:p-4 rounded-lg bg-slate-900/40 border border-slate-800/60 hover:border-slate-700 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="min-w-0">
                        <Link to={`/customers/${encodeURIComponent(c.email)}`}
                              className="text-sm text-indigo-300 hover:text-indigo-200 font-medium truncate block">
                          {c.email}
                        </Link>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {c.count} 笔 ·{' '}
                          {c.total_myr > 0 && <span className="text-amber-300/80 font-num">{fmt(c.total_myr)} MYR </span>}
                          {c.total_usdt_in > 0 && <span className="text-emerald-300/80 font-num">{fmt(c.total_usdt_in)} USDT </span>}
                        </div>
                      </div>
                      <div className="flex flex-row sm:flex-col items-end gap-3 sm:gap-0 text-right">
                        <div className="font-num text-sm text-white">Gross {fmt(c.gross_usdt)} <span className="text-slate-500 text-xs">USDT</span></div>
                        {isAdmin && (
                          <div className="font-num text-xs text-emerald-400 mt-0.5">
                            利润 {fmt((c.exchange_profit_usdt || 0) + (c.fee_usdt || 0))} USDT
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Employee performance (admin only) */}
          {isAdmin && data.employees && data.employees.length > 0 && (
            <div className="card-base p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-white mb-4">员工业绩</h2>
              <div className="space-y-2">
                {data.employees.map((e) => (
                  <div key={e.employee_id} className="p-3 rounded-lg bg-slate-900/40 border border-slate-800/60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                          {e.name?.[0]}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm text-white font-medium truncate">{e.name}</div>
                          <div className="text-xs text-slate-500 font-num">{e.count} 笔 · Gross {fmt(e.gross_usdt)} USDT</div>
                        </div>
                      </div>
                      <div className="text-right font-num">
                        <div className="text-sm text-emerald-400 font-semibold">{fmt(e.total_profit_usdt)}</div>
                        <div className="text-[10px] uppercase text-slate-500">USDT 利润</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.order_count === 0 && (
            <div className="card-base p-12 text-center">
              <Package size={40} weight="duotone" className="text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">{SCOPES.find(s=>s.v===scope)?.label}还没有订单</p>
              <Link to="/new-order" className="inline-block mt-3 text-sm text-indigo-400 hover:text-indigo-300">
                + 录第一笔订单
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
